import { createContext, useContext, useEffect, useState, ReactNode } from "react";

type Theme = "dark";

interface ThemeContextValue {
  theme: Theme;
  colorTheme: string;
  setColorTheme: (t: string) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  colorTheme: "nova",
  setColorTheme: () => {},
});

const COLOR_STORAGE_KEY = "dyna-color-theme";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme: Theme = "dark";

  // ALWAYS initialize with the SSR default so server and client first-render
  // produce identical output. Reading localStorage at first render breaks
  // hydration because the SSR build doesn't have window. The stored choice is
  // applied post-mount in the effect below; users see a brief flash of "nova"
  // before their saved theme is restored.
  const [colorTheme, setColorThemeState] = useState<string>("nova");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "dark");
    const stored = window.localStorage.getItem(COLOR_STORAGE_KEY);
    if (stored && stored !== "nova") setColorThemeState(stored);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-color", colorTheme);
    window.localStorage.setItem(COLOR_STORAGE_KEY, colorTheme);
  }, [colorTheme]);

  const setColorTheme = (ct: string) => setColorThemeState(ct);

  return (
    <ThemeContext.Provider value={{ theme, colorTheme, setColorTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
